/**
 * Picks which carousel the onboarding tour points at.
 *
 * Shared by `ProfileCarouselPage` (which tags the DOM anchors) and the tour
 * itself (which needs the profile name for its copy), so both always agree on
 * the same row.
 *
 * Warm and cold-start pages are ordered differently — warm leads with the
 * mechanical "recent purchases" row, cold start leads with a genre pick — so the
 * target is resolved by rule rather than by position.
 */

import type { RecommendationResult } from '../services/api';

/** Mechanical carousels: not a reading taste, so never a tour target. */
const MECHANICAL_CAROUSELS = new Set([
  'Based on Your Recent Purchases',
  'We Think You Might Also Like',
]);

/** Cold-start rows backfilled from editorial shelves, not from a shopper's picks. */
const EVERGREEN_SOURCE_KIND = 'editorial';

/** A carousel needs at least a partial row of books to be worth spotlighting. */
const MIN_BOOKS_TO_SPOTLIGHT = 3;

type Profile = NonNullable<RecommendationResult['purchase_profiles']>[number] & {
  source_kind?: string;
};

/** A carousel aimed at someone other than the shopper (child, partner, gift). */
export interface OtherRecipient {
  key: string;
  name: string;
  /** 'child' | 'partner' | 'gift' — drives which step copy is used. */
  type: string;
}

export interface TourTarget {
  /** Raw backend carousel key of the row the tour spotlights. */
  primaryKey: string;
  /** Profile name shown in the tour copy. */
  primaryName: string;
  /**
   * Carousel key of a row aimed at someone else (a child, partner, or gift),
   * used for the tour's third step when the shopper has one.
   * @deprecated Read from {@link otherRecipients}`[0]` instead.
   */
  otherRecipientKey?: string;
  /** @deprecated Read from {@link otherRecipients}`[0]` instead. */
  otherRecipientName?: string;
  /** @deprecated Read from {@link otherRecipients}`[0]` instead. */
  otherRecipientType?: string;
  /**
   * Other-recipient carousels for the tour, in carousel order, deduplicated by
   * `recipient_type` (first kids / partner / gift row only). Each entry becomes
   * two tour steps: section header tooltip, then carousel scroll preview
   * (auto-advances; no second tooltip).
   */
  otherRecipients: OtherRecipient[];
}

const isSelfProfile = (profile?: Profile): boolean =>
  profile?.for_self === true || profile?.recipient_type === 'self';

const isOtherRecipient = (profile?: Profile): boolean =>
  profile != null &&
  (profile.recipient_type === 'child' ||
    profile.recipient_type === 'partner' ||
    profile.recipient_type === 'gift');

/**
 * Resolve the tour's target row, or null when nothing is worth spotlighting
 * (in which case the caller should skip the tour rather than show an empty one).
 */
export function resolveTourTarget(result: RecommendationResult | null): TourTarget | null {
  if (!result?.recommendations_by_profile) return null;

  const profilesByName = new Map<string, Profile>(
    (result.purchase_profiles ?? []).map((profile) => [profile.name, profile as Profile])
  );
  const dominantNames = new Set((result.dominant_profiles ?? []).map((profile) => profile.name));

  const candidates = Object.entries(result.recommendations_by_profile)
    .filter(([key, books]) => {
      if (MECHANICAL_CAROUSELS.has(key)) return false;
      if (books.length < MIN_BOOKS_TO_SPOTLIGHT) return false;
      const profile = profilesByName.get(key);
      if (profile?.source_kind === EVERGREEN_SOURCE_KIND) return false;
      // Without a description there is nothing to explain in step 1.
      return Boolean(profile?.description);
    })
    .map(([key]) => ({ key, profile: profilesByName.get(key) }));

  if (candidates.length === 0) return null;

  // "Does this describe you?" only makes sense on a row about the shopper, so a
  // self-oriented dominant profile wins over a gift or kids one.
  const primary =
    candidates.find(({ key, profile }) => dominantNames.has(key) && isSelfProfile(profile)) ??
    candidates.find(({ key }) => dominantNames.has(key)) ??
    candidates[0];

  // One spotlight per recipient type: if a shopper has two kids carousels, the
  // tour only highlights the first — showing both is redundant and makes the
  // walkthrough feel long.
  const seenTypes = new Set<string>();
  const others: OtherRecipient[] = candidates
    .filter(({ key, profile }) => key !== primary.key && isOtherRecipient(profile))
    .reduce<OtherRecipient[]>((acc, { key, profile }) => {
      const type = profile?.recipient_type ?? 'gift';
      if (seenTypes.has(type)) return acc;
      seenTypes.add(type);
      acc.push({ key, name: profile?.name ?? key, type });
      return acc;
    }, []);

  return {
    primaryKey: primary.key,
    primaryName: primary.profile?.name ?? primary.key,
    // Backward-compat single fields derived from the first other recipient.
    otherRecipientKey: others[0]?.key,
    otherRecipientName: others[0]?.name,
    otherRecipientType: others[0]?.type,
    otherRecipients: others,
  };
}
